/**
 * EscapeFAQ — quiet FAQ section on /escape landing.
 *
 * Founder spec 18 Feb 2026 PM-14 (Wave B-1):
 *   Sits near the footer on the LANDING page only (not on
 *   active-search results). Each Q is a member-voice answer that
 *   sets expectations without marketplace marketing language.
 *
 * Accessibility: each FAQ is a native <details>/<summary>, so it's
 * keyboard-navigable and screen-reader friendly without any custom
 * disclosure JS.
 */
import React from "react";

const T = {
    ink: "#211C15", ink2: "#5C5447", ink3: "#8A8071",
    line: "#E5DCCB", brass: "#8B6914",
};

const FAQS = [
    {
        q: "What is /escape?",
        a: "/escape is the Concierge® way to discover tours, activities, attractions and day trips around the world.",
    },
    {
        q: "Who fulfils the booking?",
        a: "Many activities are fulfilled through our experience partner. Some are arranged directly by the Concierge® desk.",
    },
    {
        q: "What does \u201CBook with partner\u201D mean?",
        a: "It opens the partner booking route in a new tab. Checkout happens with the partner.",
    },
    {
        q: "What does \u201CRequest via Concierge®\u201D mean?",
        a: "The Concierge® desk will help choose, arrange or coordinate the experience.",
    },
    {
        q: "Are prices live?",
        a: "Prices come from the provider where available and may change until checkout.",
    },
    {
        q: "Why are some prices approximate?",
        a: "Some provider prices may be converted to INR for display. The final price is confirmed on the partner site or by the Concierge®.",
    },
    {
        q: "Can the Concierge® help with families, elderly guests or accessibility?",
        a: "Yes. Use Request via Concierge® and tell us who is travelling.",
    },
    {
        q: "Can the Concierge® combine activities into a day plan?",
        a: "Yes. The Concierge® can shape the day around timing, transfers, meals and pace.",
    },
    {
        q: "Are all experiences verified by Concierge®?",
        a: "The open catalogue is partner-powered. Concierge® Picks and Signature Escapes are desk-curated.",
    },
    {
        q: "What if the partner link does not work?",
        a: "Use Request via Concierge® and the Concierge® will help route it.",
    },
];

export default function EscapeFAQ({ embedded = false }) {
    return (
        <section
            data-testid="escape-faq"
            style={{ padding: embedded ? "0" : "32px 22px 64px" }}
        >
            <div style={{ maxWidth: embedded ? "100%" : 820, margin: "0 auto" }}>
                {!embedded && (
                    <>
                        <div
                            style={{
                                fontSize: 11, letterSpacing: "0.24em",
                                textTransform: "uppercase", color: T.brass,
                                fontFamily: "ui-monospace, monospace",
                                marginBottom: 8,
                            }}
                        >
                            How it works
                        </div>
                        <h2
                            className="t-serif"
                            style={{
                                fontSize: 28, color: T.ink, fontWeight: 500,
                                margin: "0 0 24px", letterSpacing: "-0.01em",
                            }}
                        >
                            Questions members ask
                        </h2>
                    </>
                )}
                <div
                    style={{
                        borderTop: `1px solid ${T.line}`,
                    }}
                >
                    {FAQS.map((f, i) => (
                        <details
                            key={f.q}
                            data-testid={`escape-faq-item-${i}`}
                            style={{
                                borderBottom: `1px solid ${T.line}`,
                                padding: "14px 0",
                            }}
                        >
                            <summary
                                style={{
                                    cursor: "pointer",
                                    fontSize: 15,
                                    color: T.ink,
                                    listStyle: "none",
                                    display: "flex",
                                    justifyContent: "space-between",
                                    alignItems: "center",
                                    fontWeight: 500,
                                }}
                            >
                                <span>{f.q}</span>
                                <span
                                    aria-hidden="true"
                                    style={{ color: T.ink3, fontSize: 18, lineHeight: 1 }}
                                >+</span>
                            </summary>
                            <p
                                style={{
                                    fontSize: 14, color: T.ink2,
                                    lineHeight: 1.6, margin: "10px 0 4px",
                                    maxWidth: 720,
                                }}
                            >
                                {f.a}
                            </p>
                        </details>
                    ))}
                </div>
            </div>
        </section>
    );
}
